import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { MessageCircle, Heart } from "lucide-react";
import SocialLinks from "@/components/SocialLinks";

function Paragraphs({ text }) {
  const paras = useMemo(() => (text || "").split("\n").filter(Boolean), [text]);
  return paras.map((p, i) => (
    <p key={`${i}-${p.slice(0, 24)}`} className="text-[var(--ink-soft)] leading-relaxed mb-4">{p}</p>
  ));
}

export default function About() {
  const [data, setData] = useState(null);
  const location = useLocation();
  useEffect(() => { api.get("/about").then((r) => setData(r.data)); }, []);
  useEffect(() => {
    if (!data || !location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  }, [data, location.hash]);

  const ourStoryParas = useMemo(
    () => (data?.our_story || "").split("\n").filter(Boolean),
    [data]
  );

  if (!data) return <div className="p-16 text-center text-[var(--ink-soft)]">Loading…</div>;

  return (
    <div data-testid="about-page">
      {/* PHOTO */}
      {data.couple_photo && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 mb-16 md:mb-24">
          <img src={data.couple_photo} alt={`${data.his_name} & ${data.her_name}`}
            className="w-full max-h-[640px] object-cover rounded-sm" />
          <div className="mt-6 flex justify-center">
            <SocialLinks variant="footer" label="Follow along" />
          </div>
        </section>
      )}

      {/* HIS STORY */}
      {data.his_story && !data.his_story.trim().startsWith("[") && (
        <section className="border-y border-[var(--line)]" style={{ background: "var(--bg-alt)" }}>
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
            <div className="grid md:grid-cols-12 gap-10">
              <div className="md:col-span-4">
                <div className="overline mb-3">His chapter</div>
                <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">{data.his_name}</h2>
              </div>
              <div className="md:col-span-8 md:pt-3"><Paragraphs text={data.his_story} /></div>
            </div>
          </div>
        </section>
      )}

      {/* HER STORY */}
      {data.her_story && !data.her_story.trim().startsWith("[") && (
        <section>
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
            <div className="grid md:grid-cols-12 gap-10">
              <div className="md:col-span-4">
                <div className="overline mb-3">Her chapter</div>
                <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">{data.her_name}</h2>
              </div>
              <div className="md:col-span-8 md:pt-3"><Paragraphs text={data.her_story} /></div>
            </div>
          </div>
        </section>
      )}

      {/* OUR STORY */}
      <section id="our-story" className="border-t border-[var(--line)]" style={{ background: "var(--forest)" }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24 text-white">
          <Heart className="w-7 h-7 text-[var(--sand)] mb-5" strokeWidth={1.25} />
          <div className="overline text-[var(--sand)] mb-3">Our chapter</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mb-8 leading-tight max-w-3xl">
            Two different stories. One unbreakable promise.
          </h2>
          <div className="max-w-3xl text-white/85 text-lg leading-relaxed space-y-4">
            {ourStoryParas.map((p, i) => <p key={`${i}-${p.slice(0, 24)}`}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* CLOSING + CTA */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
        <MessageCircle className="w-7 h-7 text-[var(--forest)] mx-auto mb-6" strokeWidth={1.25} />
        <p className="font-serif-display text-2xl md:text-3xl leading-relaxed text-[var(--ink)] italic mb-10">
          &ldquo;{data.closing}&rdquo;
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/messages" data-testid="about-cta-message"
            className="bg-[var(--terracotta)] text-[var(--ink)] px-7 py-3.5 rounded-full text-sm hover:bg-[var(--terracotta-dark)] transition">
            Send us a note
          </Link>
          <Link to="/travels" data-testid="about-cta-travels"
            className="border border-[var(--ink)] px-7 py-3.5 rounded-full text-sm hover:bg-[var(--ink)] hover:text-[var(--bg)] transition">
            See where we are
          </Link>
        </div>
      </section>
    </div>
  );
}
